import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { EMPTY, Observable, map, tap } from 'rxjs';
import { Booking, BookingPlayer, BookingStatus } from '../model/booking.model';
import { environment } from '../../environments/environment';
import { AuthService } from '../auth/auth.service';

export type AlertScope = 'active' | 'past' | 'all';

interface AlertCriteriaDto {
  isoDate?: string;
  dayOfWeek?: string;
  dayOfMonth?: number;
  monthShort?: string;
  year?: number;
  appointmentTime?: string;
  displayTime?: string;
  players?: number;
  holes?: number;
  pricePerPatron?: number;
}

interface AlertCourseDto {
  name?: string;
  location?: string;
  locationDisplay?: string;
  countryCode?: string;
  country?: string;
  locationId?: string;
}

interface AlertCustomerDto {
  name?: string;
  email?: string;
  phone?: string;
  avatarUrl?: string | null;
}

interface AlertDto {
  id?: string;
  externalId?: string;
  status?: string;
  statusLabel?: string;
  statusColor?: string;
  course?: AlertCourseDto;
  criteria?: AlertCriteriaDto;
  customer?: AlertCustomerDto;
  notificationChannel?: string;
  notifiedAt?: string | null;
  expiresAt?: string;
  chatId?: string;
  place?: string;
  coordinates?: { lat?: number; lng?: number } | null;
  createdAt?: string;
  updatedAt?: string;
}

interface AlertsResponse {
  results?: AlertDto[];
  data?: AlertDto[];
  scope?: AlertScope;
  page?: number;
  limit?: number;
  totalPages?: number;
  totalResults?: number;
  activeCount?: number;
  pastCount?: number;
}

export interface AlertsPageOptions {
  page?: number;
  limit?: number;
}

@Injectable({ providedIn: 'root' })
export class AlertsService {
  private readonly _http = inject(HttpClient);
  private readonly _authService = inject(AuthService);

  private readonly _activeAlerts = signal<Booking[]>([]);
  private readonly _pastAlerts = signal<Booking[]>([]);
  private readonly _loading = signal(false);
  private readonly _activeCount = signal(0);
  private readonly _pastCount = signal(0);

  readonly activeAlerts = this._activeAlerts.asReadonly();
  readonly pastAlerts = this._pastAlerts.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly activeCount = this._activeCount.asReadonly();
  readonly pastCount = this._pastCount.asReadonly();

  getById(id: string): Booking | undefined {
    return (
      this._activeAlerts().find((a) => a.alertId === id || a.id === id) ??
      this._pastAlerts().find((a) => a.alertId === id || a.id === id)
    );
  }

  loadAlerts(scope: AlertScope = 'active', opts: AlertsPageOptions = {}): Observable<Booking[]> {
    if (!this._authService.isSignedIn()) return EMPTY;
    this._loading.set(true);
    const { page = 1, limit = 20 } = opts;
    const params = new HttpParams()
      .set('scope', scope)
      .set('page', page)
      .set('limit', limit);
    return this._http
      .get<AlertsResponse | AlertDto[]>(`${environment.apiUrl}alerts`, { params })
      .pipe(
        tap((res) => this.captureCounts(res)),
        map((res) =>
          Array.isArray(res) ? res : res?.results ?? res?.data ?? [],
        ),
        map((dtos: AlertDto[]) => dtos.map((dto) => this.mapDtoToBooking(dto))),
        tap({
          next: (rows) => {
            if (scope === 'past') {
              this._pastAlerts.set(rows);
            } else {
              this._activeAlerts.set(rows);
            }
            this._loading.set(false);
          },
          error: () => this._loading.set(false),
        }),
      );
  }

  loadAlertById(id: string): Observable<Booking> {
    return this._http
      .get<AlertDto | { alert?: AlertDto; data?: AlertDto }>(`${environment.apiUrl}alerts/${id}`)
      .pipe(
        map((res) => {
          if (res && typeof res === 'object' && 'alert' in res && res.alert) return res.alert;
          if (res && typeof res === 'object' && 'data' in res && res.data) return res.data;
          return res as AlertDto;
        }),
        map((dto) => this.mapDtoToBooking(dto)),
        tap((row) => {
          // Upsert so a direct/refreshed detail load (where the list is empty) still
          // caches the detail for getById() to return.
          const list = this._activeAlerts();
          const idx = list.findIndex((a) => a.alertId === row.alertId || a.id === row.id);
          if (idx >= 0) {
            const next = [...list];
            next[idx] = row;
            this._activeAlerts.set(next);
          } else {
            this._activeAlerts.set([...list, row]);
          }
        }),
      );
  }

  private captureCounts(res: AlertsResponse | AlertDto[]): void {
    if (Array.isArray(res)) return;
    if (typeof res?.activeCount === 'number') this._activeCount.set(res.activeCount);
    if (typeof res?.pastCount === 'number') this._pastCount.set(res.pastCount);
  }

  private mapDtoToBooking(dto: AlertDto): Booking {
    const isoDate = dto.criteria?.isoDate;
    const apptTime = dto.criteria?.appointmentTime;
    const rawDate = apptTime ?? isoDate;
    const parsed = rawDate ? new Date(rawDate) : null;
    const date = parsed && !Number.isNaN(parsed.getTime()) ? parsed : new Date();

    const playerCount = dto.criteria?.players ?? 0;
    const players: BookingPlayer[] = Array.from({ length: playerCount }, () => ({} as BookingPlayer));

    const status = this.mapAlertStatus(dto.status);
    const statusLabel = dto.statusLabel ?? this.defaultLabel(dto.status);
    const statusColor = dto.statusColor ?? this.defaultColor(dto.status);

    const rawCoords = dto.coordinates;
    const coordinates =
      rawCoords && typeof rawCoords.lat === 'number' && typeof rawCoords.lng === 'number'
        ? { lat: rawCoords.lat, lng: rawCoords.lng }
        : undefined;

    const alertId = dto.id ?? '';

    return {
      id: alertId || dto.externalId || '',
      date,
      time: dto.criteria?.displayTime ?? '',
      status,
      statusLabel,
      statusColor,
      courseName: dto.course?.name ?? '',
      location: dto.course?.locationDisplay || dto.course?.location || '',
      locationId: dto.course?.locationId,
      place: dto.place,
      coordinates,
      players,
      holes: dto.criteria?.holes,
      apptTime,
      contact: dto.customer
        ? {
            name: dto.customer.name,
            email: dto.customer.email,
            phone: dto.customer.phone,
            avatarUrl: dto.customer.avatarUrl ?? undefined,
          }
        : undefined,
      isAlert: true,
      alertId,
      alertReferenceId: dto.externalId,
      notificationChannel: dto.notificationChannel,
      notifiedAt: dto.notifiedAt ?? undefined,
      expiresAt: dto.expiresAt,
      requestedDate: isoDate,
    };
  }

  private mapAlertStatus(raw: string | undefined): BookingStatus {
    switch ((raw ?? '').toLowerCase()) {
      case 'notified':
        return 'booked';
      case 'expired':
      case 'cancelled':
      case 'notify_failed':
        return 'cancelled';
      case 'processing':
        return 'processing';
      case 'active':
      default:
        return 'waitlist';
    }
  }

  private defaultLabel(raw: string | undefined): string {
    switch ((raw ?? '').toLowerCase()) {
      case 'active': return 'Alert';
      case 'notified': return 'Notified';
      case 'expired': return 'Expired';
      case 'cancelled': return 'Cancelled';
      case 'notify_failed': return 'Expired';
      case 'processing': return 'Processing';
      default: return 'Alert';
    }
  }

  private defaultColor(raw: string | undefined): string {
    switch ((raw ?? '').toLowerCase()) {
      case 'notified': return 'green';
      case 'expired':
      case 'cancelled': return 'grey';
      case 'notify_failed': return 'red';
      case 'processing': return 'yellow';
      case 'active':
      default: return 'blue';
    }
  }
}
